import { WaitlistEntry } from '@shared/types';

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
  waitlist: WaitlistEntry[];
  tableName: string;
  displayNames?: Map<string, string>;
}

export function WaitlistModal({ isOpen, onClose, waitlist, tableName, displayNames }: WaitlistModalProps) {
  if (!isOpen) return null;

  // Filter only waiting entries and sort by position
  const waitingEntries = waitlist
    .filter(entry => entry.status === 'waiting')
    .sort((a, b) => {
      // Sort by position field (which preserves the correct order)
      return (a.position || 0) - (b.position || 0);
    });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Waitlist</h2>
            <p className="text-sm text-gray-600">{tableName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close waitlist"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[60vh]">
          {waitingEntries.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-gray-400 mb-2">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <p className="text-gray-500 text-sm">No one is currently waiting</p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {waitingEntries.map((entry, index) => (
                <div
                  key={entry.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    {/* Position number */}
                    <div className="flex-shrink-0 w-8 h-8 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center text-sm font-semibold">
                      {index + 1}
                    </div>
                    
                    {/* Player name */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {displayNames?.get(entry.id) || entry.name}
                      </p>
                    </div>
                  </div>

                  {/* Self-added indicator */}
                  {entry.added_by === 'self' && (
                    <div className="flex-shrink-0 ml-2" title="Added via website">
                      <span className="text-lg">🌐</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-center">
            <p className="text-xs text-gray-500">
              {waitingEntries.length} {waitingEntries.length === 1 ? 'person' : 'people'} waiting
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
